import type {
  CardCondition,
  LotStatus,
  ProductStatus,
} from "@/context/AppContext";

export type BadgeVariant =
  | "default"
  | "success"
  | "warning"
  | "danger"
  | "muted"
  | "accent";

interface BadgeProps {
  label: string;
  variant?: BadgeVariant;
}

const variantClass: Record<BadgeVariant, string> = {
  default: "bg-primary text-primary-foreground",
  success: "bg-success text-success-foreground",
  warning: "bg-warning text-warning-foreground",
  danger: "bg-destructive text-destructive-foreground",
  muted: "bg-muted text-muted-foreground",
  accent: "bg-secondary text-secondary-foreground",
};

export function Badge({ label, variant = "default" }: BadgeProps) {
  return (
    <span
      className={`inline-flex self-start rounded-full px-2 py-[3px] text-[11px] font-semibold tracking-wide ${variantClass[variant]}`}
    >
      {label}
    </span>
  );
}

export function lotStatusBadge(status: LotStatus): BadgeVariant {
  const map: Record<LotStatus, BadgeVariant> = {
    Unsorted: "warning",
    Sorting: "warning",
    Sorted: "default",
    Listed: "accent",
    "Partially Sold": "success",
    Closed: "muted",
  };
  return map[status] ?? "default";
}

export function lotTypeBadge(type: string): BadgeVariant {
  const map: Record<string, BadgeVariant> = {
    Collection: "muted",
    "Booster Pack": "accent",
    "Booster Box": "success",
    "Elite Trainer Box": "success",
    "Special Pack": "warning",
    Bulk: "muted",
    // Magic / generic
    Bundle: "accent",
    "Commander Deck": "success",
    // Yu-Gi-Oh / One Piece
    "Structure Deck": "success",
    "Starter Deck": "success",
    Tin: "warning",
  };
  return map[type] ?? "muted";
}

export function productStatusBadge(status: ProductStatus): BadgeVariant {
  const map: Record<ProductStatus, BadgeVariant> = {
    Draft: "muted",
    "Ready to List": "accent",
    Listed: "default",
    Sold: "success",
    Picked: "success",
    Packed: "success",
    Shipped: "success",
  };
  return map[status] ?? "muted";
}

export function conditionBadge(condition: CardCondition): BadgeVariant {
  const map: Record<CardCondition, BadgeVariant> = {
    NM: "success",
    LP: "accent",
    MP: "warning",
    HP: "danger",
    Damaged: "danger",
  };
  return map[condition] ?? "muted";
}
